import { m, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

type RevealProps = {
  children: ReactNode;
  /** Delay in seconds before the reveal starts. */
  delay?: number;
  /** Vertical travel distance in px (ignored when reduced-motion is on). */
  y?: number;
  /** Animation duration in seconds. */
  duration?: number;
  className?: string;
};

/**
 * Reusable scroll-triggered reveal. Rises + fades content into view once,
 * with a premium easing curve. Honors `prefers-reduced-motion` by falling
 * back to a plain fade (no movement). Built to be reused across pages.
 */
export function Reveal({
  children,
  delay = 0,
  y = 28,
  duration = 0.7,
  className,
}: RevealProps) {
  const reduce = useReducedMotion();
  return (
    <m.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </m.div>
  );
}
